import {
	Badge,
	Box,
	Button,
	Divider,
	Inline,
	RelativeTime,
	Stack,
	Text,
} from "@sembark-travel/ui-base"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import { useCallback, useState } from "react"
import { JSONPreview } from "../Integrations/JsonPreview"
import {
	TAutomationRun,
	TAutomationRunDetail,
	TAutomationStepRun,
	XHR,
} from "./store"
import { useXHR } from "@sembark-travel/xhr"

function statusBadge(status: string, title?: string | null) {
	return (
		<Badge
			success={status === "completed"}
			warning={status === "pending" || status === "running"}
			danger={status === "failed" || status === "cancelled"}
			title={title || undefined}
		>
			{status}
		</Badge>
	)
}

function stepTitle(stepRun: TAutomationStepRun) {
	const step = stepRun.automation_step
	if (!step) {
		return `Step run #${stepRun.id}`
	}

	if (step.type === "action") {
		const actionType = String(
			(step.config as { action?: { type?: string } })?.action?.type || "action"
		)
		return `Step ${step.position}: ${actionType}`
	}

	return `Step ${step.position}: ${step.type}`
}

type TTraceNode = {
	stepRun: TAutomationStepRun
	children: Array<TTraceNode>
}

function buildTraceTree(stepRuns: Array<TAutomationStepRun>) {
	const nodes = new Map<number, TTraceNode>()
	const nodesByStepId = new Map<number, TTraceNode>()
	const roots: Array<TTraceNode> = []

	for (const stepRun of stepRuns) {
		const node = {
			stepRun,
			children: [],
		}
		nodes.set(stepRun.id, node)

		if (stepRun.automation_step?.id) {
			nodesByStepId.set(stepRun.automation_step.id, node)
		}
	}

	for (const node of nodes.values()) {
		const parentId = node.stepRun.automation_step?.parent_id
		const parent = parentId ? nodesByStepId.get(parentId) : undefined

		if (parent) {
			parent.children.push(node)
			continue
		}

		roots.push(node)
	}

	const sortNodes = (items: Array<TTraceNode>) => {
		items.sort(
			(left, right) =>
				(left.stepRun.automation_step?.position || 0) -
				(right.stepRun.automation_step?.position || 0)
		)
		items.forEach((item) => sortNodes(item.children))
	}

	sortNodes(roots)

	return roots
}

function runContextLabel(run: TAutomationRun) {
	if (!run.triggered_by_type || !run.triggered_by_id) {
		return "System"
	}

	return `${run.triggered_by_type} #${run.triggered_by_id}`
}

function provenanceLines(run: TAutomationRun) {
	const lines: Array<string> = []
	const event = run.provenance?.event
	const schedule = run.provenance?.schedule

	if (event?.name || run.event_name) {
		lines.push(`Started from event: ${event?.name || run.event_name}`)
	}

	if (event?.changed_fields?.length) {
		lines.push(`Changed fields: ${event.changed_fields.join(", ")}`)
	}

	if (schedule?.automation_scheduled_run_id) {
		lines.push(`Scheduled occurrence: #${schedule.automation_scheduled_run_id}`)
	}

	if (schedule?.checkpoint_key) {
		lines.push(`Checkpoint: ${schedule.checkpoint_key}`)
	}

	if (typeof schedule?.occurrence_index === "number") {
		lines.push(`Occurrence index: ${schedule.occurrence_index}`)
	}

	if (schedule?.field) {
		lines.push(`Schedule field: ${schedule.field}`)
	}

	if (schedule?.mode) {
		lines.push(`Schedule mode: ${schedule.mode}`)
	}

	return lines
}

function StepRunCard({
	node,
	depth = 0,
}: {
	node: TTraceNode
	depth?: number
}) {
	const { stepRun, children } = node

	return (
		<Box
			borderWidth="1"
			rounded="md"
			padding="4"
			marginLeft={depth ? "6" : undefined}
		>
			<Stack gap="4">
				<Inline alignItems="center" justifyContent="between" gap="3">
					<Stack gap="1">
						<Text fontWeight="semibold">{stepTitle(stepRun)}</Text>
						<Text color="muted" fontSize="sm">
							{stepRun.automation_step
								? `Type: ${stepRun.automation_step.type}`
								: stepRun.step_type}
						</Text>
					</Stack>
					{statusBadge(stepRun.status, stepRun.error)}
				</Inline>

				<Inline gap="6" flexWrap="wrap">
					<Stack gap="1">
						<Text color="muted" fontSize="sm">
							Started
						</Text>
						{stepRun.started_at ? (
							<RelativeTime timestamp={stepRun.started_at} />
						) : (
							<Text color="muted">Not started</Text>
						)}
					</Stack>
					<Stack gap="1">
						<Text color="muted" fontSize="sm">
							Finished
						</Text>
						{stepRun.completed_at ||
						stepRun.failed_at ||
						stepRun.cancelled_at ? (
							<RelativeTime
								timestamp={
									stepRun.completed_at ||
									stepRun.failed_at ||
									stepRun.cancelled_at ||
									undefined
								}
							/>
						) : stepRun.resume_at ? (
							<Text color="muted">Waiting to resume</Text>
						) : (
							<Text color="muted">In progress</Text>
						)}
					</Stack>
				</Inline>

				{stepRun.error ? (
					<Box
						bgColor="danger"
						borderColor="danger"
						borderWidth="1"
						rounded="md"
						padding="3"
					>
						<Text color="danger" fontSize="sm">
							{stepRun.error}
						</Text>
					</Box>
				) : stepRun.status === "skipped" ? (
					<Text color="muted" fontSize="sm">
						Skipped because{" "}
						{String(
							stepRun.meta?.skipped_by_step_key || "an earlier condition"
						)}{" "}
						did not match.
					</Text>
				) : stepRun.status === "reused" ? (
					<Text color="muted" fontSize="sm">
						Reused from Run #
						{String(stepRun.meta?.reused_from_run_id || "unknown")}
						{stepRun.meta?.original_status
							? ` after originally finishing as ${String(stepRun.meta.original_status)}.`
							: "."}
					</Text>
				) : null}

				<Divider />

				<Stack gap="4">
					<Stack gap="2">
						<Text fontWeight="semibold">Configured Step</Text>
						<JSONPreview
							data={
								(stepRun.automation_step as unknown as
									| Record<string, unknown>
									| undefined) || undefined
							}
						/>
					</Stack>

					<Stack gap="2">
						<Text fontWeight="semibold">Execution Input</Text>
						<JSONPreview
							data={
								(stepRun.input as Record<string, unknown> | undefined) ||
								undefined
							}
						/>
					</Stack>

					<Stack gap="2">
						<Text fontWeight="semibold">Execution Output</Text>
						<JSONPreview
							data={
								(stepRun.output as Record<string, unknown> | undefined) ||
								undefined
							}
						/>
					</Stack>

					<Stack gap="2">
						<Text fontWeight="semibold">Execution Meta</Text>
						<JSONPreview
							data={
								(stepRun.meta as Record<string, unknown> | undefined) ||
								undefined
							}
						/>
					</Stack>
				</Stack>

				{children.length ? (
					<Stack gap="3">
						<Text fontWeight="semibold">Nested Steps</Text>
						{children.map((child) => (
							<StepRunCard
								key={child.stepRun.id}
								node={child}
								depth={depth + 1}
							/>
						))}
					</Stack>
				) : null}
			</Stack>
		</Box>
	)
}

export function RunTraceDialog({
	run,
	children,
}: {
	run: TAutomationRun
	children?: (props: { open: () => void }) => React.ReactNode
}) {
	const xhr = useXHR()
	const [isOpen, openDialog, closeDialog] = useDialog()
	const [detail, setDetail] = useState<TAutomationRunDetail | null>(null)
	const [loading, setLoading] = useState(false)

	const handleOpen = useCallback(async () => {
		openDialog()
		setLoading(true)
		try {
			const response = await XHR(xhr).showRun(run.id)
			setDetail(response.data)
		} finally {
			setLoading(false)
		}
	}, [openDialog, run.id, xhr])

	const activeRun: TAutomationRunDetail = detail || run

	return (
		<>
			{children ? (
				children({ open: handleOpen })
			) : (
				<Button size="sm" level="secondary" onClick={handleOpen}>
					View Trace
				</Button>
			)}
			<Dialog
				open={isOpen}
				onClose={closeDialog}
				title={`Run #${run.id} Trace`}
				xl
			>
				<Dialog.Body>
					<Stack gap="6">
						<Box borderWidth="1" rounded="md" padding="4">
							<Stack gap="3">
								<Inline alignItems="center" justifyContent="between" gap="3">
									<Stack gap="1">
										<Text fontWeight="semibold">
											{activeRun.automation?.source?.name ||
												activeRun.automation?.source_key ||
												activeRun.source_key}
										</Text>
										<Text color="muted" fontSize="sm">
											{runContextLabel(activeRun)}
										</Text>
									</Stack>
									<Inline gap="2" alignItems="center">
										<Badge warning={activeRun.run_mode === "dry_run"}>
											{activeRun.run_mode === "dry_run" ? "Dry Run" : "Live"}
										</Badge>
										{statusBadge(activeRun.status, activeRun.error)}
									</Inline>
								</Inline>

								{activeRun.retry?.retried_from_run_id ? (
									<Text color="muted" fontSize="sm">
										{activeRun.retry.mode === "resume_from_failed_step"
											? "Resumed from failed step"
											: "Started over"}{" "}
										from Run #{activeRun.retry.retried_from_run_id}
										{activeRun.retry.resumed_from_step_key
											? ` at step ${activeRun.retry.resumed_from_step_key}`
											: ""}
										.
									</Text>
								) : null}

								{provenanceLines(activeRun).length ? (
									<Stack gap="1">
										<Text fontWeight="semibold">Why This Run Started</Text>
										{provenanceLines(activeRun).map((line) => (
											<Text key={line} color="muted" fontSize="sm">
												{line}
											</Text>
										))}
										{activeRun.provenance?.schedule?.scheduled_for ? (
											<Text color="muted" fontSize="sm">
												Scheduled for{" "}
												<RelativeTime
													timestamp={
														activeRun.provenance.schedule.scheduled_for
													}
												/>
											</Text>
										) : null}
									</Stack>
								) : null}

								{activeRun.error ? (
									<Box
										bgColor="danger"
										borderColor="danger"
										borderWidth="1"
										rounded="md"
										padding="3"
									>
										<Text color="danger" fontSize="sm">
											{activeRun.error}
										</Text>
									</Box>
								) : null}

								<Stack gap="2">
									<Text fontWeight="semibold">Run Context</Text>
									<JSONPreview
										data={
											(activeRun.context as
												| Record<string, unknown>
												| undefined) || undefined
										}
									/>
								</Stack>
							</Stack>
						</Box>

						<Stack gap="3">
							<Text fontWeight="semibold">Step Execution Trace</Text>
							{loading && !detail ? (
								<Text color="muted">Loading trace…</Text>
							) : activeRun.step_runs?.length ? (
								<Stack gap="4">
									{buildTraceTree(activeRun.step_runs).map((node) => (
										<StepRunCard key={node.stepRun.id} node={node} />
									))}
								</Stack>
							) : (
								<Text color="muted">No step runs recorded yet.</Text>
							)}
						</Stack>
					</Stack>
				</Dialog.Body>
			</Dialog>
		</>
	)
}
